import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Maximize2, Loader2 } from 'lucide-react';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Configurar el worker de pdf.js — desde unpkg (sirve directo desde npm).
// cdnjs.cloudflare.com NO tiene aún la v5 de pdfjs-dist y devuelve 404.
pdfjs.GlobalWorkerOptions.workerSrc =
  `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Base URL para los recursos auxiliares que pdf.js v5 necesita para
// decodificar contenido del PDF (fuentes estándar, mapas de caracteres,
// e imágenes JPEG2000/JBIG2). Si no se proveen, pdf.js renderiza solo el
// texto vectorial y omite imágenes raster, gráficos complejos, etc.
const PDFJS_CDN = `https://unpkg.com/pdfjs-dist@${pdfjs.version}`;

interface PDFViewerModalProps {
  open: boolean;
  url: string | null;
  title: string;
  onClose: () => void;
}

/**
 * Visor PDF en pantalla completa con bloqueo máximo de descarga.
 *
 * Diseño anti-descarga (capa por capa):
 *   1. Descarga el PDF como Blob via fetch → ObjectURL temporal.
 *      Así la URL original del Storage NO queda visible en `<a href>` ni
 *      en el src del iframe.
 *   2. Renderiza con pdf.js como CANVAS — no como iframe.
 *      No hay toolbar del browser (botón "Download" / "Print").
 *   3. Watermark con email del usuario superpuesto en cada página.
 *      Si alguien hace screenshot, queda identificado.
 *   4. Bloquea click-derecho, Ctrl+S, Ctrl+P, Ctrl+A, Ctrl+C, F12,
 *      arrastrar imagen, selección de texto.
 *   5. Imprimir desde el navegador queda en blanco (CSS @media print).
 *
 * Aclaración honesta: nada en la web previene 100% que un usuario muy
 * técnico descargue. Pero esta capa para el 99%.
 */
export function PDFViewerModal({ open, url, title, onClose }: PDFViewerModalProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  const [blobUrl, setBlobUrl]   = useState<string | null>(null);
  const [loadErr, setLoadErr]   = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number>(0);
  const [page,    setPage]      = useState<number>(1);
  const [scale,   setScale]     = useState<number>(1.2);

  // Opciones de Document. useMemo es CRÍTICO acá: si las pasamos inline,
  // React las recrea en cada render y react-pdf re-carga el PDF en loop.
  // Las URLs son ESTÁTICAS, así que sin deps.
  //
  // wasmUrl: pdf.js v5 decodifica imágenes JPEG 2000 (.jpx/.jp2) con
  // OpenJPEG WebAssembly. Si esta opción falta, las imágenes JPX salen
  // en blanco con error "OpenJPEG failed to initialize" en la consola.
  // Este es el bug real por el que los PDFs no mostraban capturas de
  // pantalla — son imágenes JPEG 2000 (común en PowerPoint export).
  const documentOptions = useMemo(() => ({
    cMapUrl:             `${PDFJS_CDN}/cmaps/`,
    cMapPacked:          true,
    standardFontDataUrl: `${PDFJS_CDN}/standard_fonts/`,
    wasmUrl:             `${PDFJS_CDN}/wasm/`,
    iccUrl:              `${PDFJS_CDN}/iccs/`,
    enableXfa: true,
  }), []);

  // Descargar PDF como blob al abrir
  useEffect(() => {
    if (!open || !url) return;
    let cancelled = false;
    let createdBlobUrl: string | null = null;

    (async () => {
      try {
        setLoadErr(null);
        setBlobUrl(null);
        const res = await fetch(url, { credentials: 'omit' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        createdBlobUrl = URL.createObjectURL(blob);
        if (!cancelled) setBlobUrl(createdBlobUrl);
      } catch (e: any) {
        if (!cancelled) setLoadErr(e?.message ?? 'No se pudo cargar el PDF');
      }
    })();

    return () => {
      cancelled = true;
      if (createdBlobUrl) URL.revokeObjectURL(createdBlobUrl);
    };
  }, [open, url]);

  // Bloqueos de teclado mientras está abierto
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKey = (e: KeyboardEvent) => {
      // ESC cierra
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      // Bloquear combinaciones típicas de descarga / impresión / devtools
      const k = e.key.toLowerCase();
      const blockSet =
        (e.ctrlKey || e.metaKey) && ['s', 'p', 'a', 'c', 'u'].includes(k); // save/print/select-all/copy/view-source
      if (blockSet || k === 'f12') {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    document.addEventListener('keydown', onKey, { capture: true });

    return () => {
      document.removeEventListener('keydown', onKey, { capture: true } as any);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  // Reset estado interno al cerrar
  useEffect(() => {
    if (open) return;
    setPage(1);
    setScale(1.2);
    setNumPages(0);
  }, [open]);

  if (!open) return null;

  const node = (
    <div
      ref={containerRef}
      role="dialog"
      aria-modal="true"
      aria-label={`Visor PDF: ${title}`}
      className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex flex-col select-none"
      // CSS user-select none reduce que se copie el texto
      style={{ WebkitUserSelect: 'none', userSelect: 'none' }}
      onContextMenu={(e) => e.preventDefault()}
      onDragStart={(e) => e.preventDefault()}
    >
      {/* Header / toolbar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.08] bg-cubus-deep/80 backdrop-blur">
        <div className="min-w-0">
          <div className="text-[10px] tracking-[2px] uppercase font-semibold text-cubus-lime mb-0.5">
            Visor PDF · Solo lectura
          </div>
          <h2 className="text-sm font-medium text-white truncate max-w-[60vw]">{title}</h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setScale(s => Math.max(0.5, s - 0.15))}
            className="p-2 rounded-lg bg-white/[0.05] hover:bg-white/[0.10] text-white/80"
            title="Reducir zoom"
            aria-label="Reducir zoom"
          >
            <ZoomOut className="w-4 h-4" />
          </button>
          <span className="text-xs text-white/60 tabular-nums min-w-[3rem] text-center">
            {Math.round(scale * 100)}%
          </span>
          <button
            onClick={() => setScale(s => Math.min(3, s + 0.15))}
            className="p-2 rounded-lg bg-white/[0.05] hover:bg-white/[0.10] text-white/80"
            title="Aumentar zoom"
            aria-label="Aumentar zoom"
          >
            <ZoomIn className="w-4 h-4" />
          </button>
          <button
            onClick={() => setScale(1.2)}
            className="p-2 rounded-lg bg-white/[0.05] hover:bg-white/[0.10] text-white/80"
            title="Tamaño original"
            aria-label="Tamaño original"
          >
            <Maximize2 className="w-4 h-4" />
          </button>
          <div className="w-px h-6 bg-white/[0.1] mx-1" />
          <button
            onClick={onClose}
            className="group flex items-center gap-2 px-3 py-2 rounded-lg bg-white/[0.05] hover:bg-white/[0.10] border border-white/10 hover:border-cubus-lime/40 text-white/80 transition-all"
            title="Cerrar (Esc)"
            aria-label="Cerrar"
          >
            <span className="text-sm">Cerrar</span>
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Cuerpo: canvas del PDF + watermark superpuesto */}
      <div className="flex-1 overflow-auto scroll-cubus relative pdf-viewer-body">
        <div className="min-h-full flex items-start justify-center p-6">
          {!blobUrl && !loadErr && (
            <div className="flex flex-col items-center gap-3 mt-20 text-white/70">
              <Loader2 className="w-8 h-8 animate-spin" />
              <p className="text-sm">Cargando PDF…</p>
            </div>
          )}
          {loadErr && (
            <div className="mt-20 text-center">
              <p className="text-sm text-rose-400">No se pudo cargar el PDF.</p>
              <p className="text-xs text-white/40 mt-1">{loadErr}</p>
            </div>
          )}
          {blobUrl && (
            <div className="relative">
              <Document
                file={blobUrl}
                options={documentOptions}
                onLoadSuccess={(d) => setNumPages(d.numPages)}
                onLoadError={(e) => setLoadErr(e.message)}
                loading={
                  <div className="flex flex-col items-center gap-2 text-white/60 mt-10">
                    <Loader2 className="w-6 h-6 animate-spin" />
                    <span className="text-xs">Procesando…</span>
                  </div>
                }
              >
                <Page
                  pageNumber={page}
                  scale={scale}
                  // Sin text layer ni annotation layer — solo el canvas
                  // que es donde van las imágenes. Esto descarta que
                  // algún overlay del textLayer tape el contenido.
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  className="shadow-2xl pdf-page-canvas"
                />
              </Document>

              {/* Watermark eliminado a pedido del usuario.
                  El bloqueo de descarga sigue intacto: el PDF se renderiza
                  como canvas (sin toolbar nativo), está envuelto en blob URL
                  temporal y los atajos Ctrl+S / Ctrl+P / click-derecho /
                  drag / F12 están todos bloqueados. */}
            </div>
          )}
        </div>
      </div>

      {/* Footer: navegación de páginas */}
      {numPages > 1 && (
        <div className="flex items-center justify-center gap-3 px-4 py-3 border-t border-white/[0.08] bg-cubus-deep/80 backdrop-blur">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="p-2 rounded-lg bg-white/[0.05] hover:bg-white/[0.10] text-white/80 disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label="Página anterior"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm text-white/80 tabular-nums min-w-[6rem] text-center">
            Página {page} / {numPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(numPages, p + 1))}
            disabled={page >= numPages}
            className="p-2 rounded-lg bg-white/[0.05] hover:bg-white/[0.10] text-white/80 disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label="Página siguiente"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Estilos:
            1. Imprimir desde el browser → pantalla en blanco
            2. Text layer del PDF: invisible pero ahí (necesario para que el
               pipeline renderice imágenes). Sin selección de texto. */}
      <style>{`
        @media print {
          .pdf-viewer-body * { visibility: hidden !important; }
          .pdf-viewer-body::after {
            content: "Contenido protegido — Solo lectura. CUBUS Latam.";
            visibility: visible;
            position: fixed;
            top: 50%; left: 50%;
            transform: translate(-50%, -50%);
            font-size: 14pt;
            font-family: sans-serif;
          }
        }
        /* Bloquear selección y copia desde el text layer del PDF */
        .pdf-viewer-body .react-pdf__Page__textContent,
        .pdf-viewer-body .react-pdf__Page__textContent * {
          user-select: none !important;
          -webkit-user-select: none !important;
          pointer-events: none !important;
        }
      `}</style>
    </div>
  );

  return createPortal(node, document.body);
}
